import React, { useCallback, useRef, memo, useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';
import { useTypedSelector } from '@hooks';
import { useCurrentWorkspace } from '@hooks/workspace';
import { toggleUploadAssetModal } from '@redux/actions/modalAction';
import { setAsset, setAssetVariant } from '@redux/actions/mediaViewerAction';
import { LgWidth } from '@components/Responsive';
import DropdownAssetVersions from '@components/MediaViewerHeader/components/DropdownAssetVersions';
import { ArrowLeftWithoutLineIcon, PlusIcon } from '@icons';
import './HeaderBreadcrumbs.less';

type HeaderApprovalBreadcrumbsProps = {
  approvalWorkflowId?: string;
  password?: string | null;
  view?: 'approval-assets-list' | 'approval-asset';
};

export default memo(function HeaderApprovalBreadcrumbs({
  approvalWorkflowId,
  password,
  view = 'approval-assets-list'
}: HeaderApprovalBreadcrumbsProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();

  const assets = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const assetApprovalWorkflowId = assets?.asset?.approvalWorkflow?.id;
  const awId = approvalWorkflowId || assetApprovalWorkflowId || '';
  const isAssetView = view === 'approval-asset';

  const { data } = useGetApprovalWorkflow({
    enabled: !!awId,
    params: {
      id: awId,
      password
    }
  });

  const approvalWorkflow = data?.approvalWorkflow;
  const canManage = !!approvalWorkflow?.permissions.manage;

  const locationStateRef = useRef<{ returnTo: string } | undefined>(undefined);

  const [currentWorkspace] = useCurrentWorkspace(false);
  const approvalWorkspace =
    currentWorkspace?.id === approvalWorkflow?.workspaceId
      ? currentWorkspace
      : undefined;

  useEffect(() => {
    if (!state) return;
    locationStateRef.current = state;
  }, [state]);

  const goToApprovals = useCallback(() => {
    if (!approvalWorkspace) return;
    const tab = canManage ? 'manage' : 'review';
    navigate(`/approvals/${approvalWorkspace.id}?tab=${tab}`);
  }, [approvalWorkspace, canManage, navigate]);

  const goToApprovalDetail = useCallback(() => {
    if (!approvalWorkflow) return;
    navigate(
      `/campaigns/approvals/${approvalWorkflow.workspaceId}/${approvalWorkflow.campaignId}/${approvalWorkflow.id}`
    );
  }, [approvalWorkflow, navigate]);

  const goToAssetList = useCallback(() => {
    if (!approvalWorkflow) return;
    dispatch(setAsset(null));
    dispatch(setAssetVariant(null));
    navigate(`/approval?id=${approvalWorkflow.id}&forceBack=true`);
  }, [approvalWorkflow, dispatch, navigate]);

  const goBack = useCallback(() => {
    const returnTo = locationStateRef.current?.returnTo;
    if (returnTo) {
      locationStateRef.current = undefined;
      navigate(returnTo);
      return;
    }
    if (isAssetView) {
      goToAssetList();
      return;
    }
    goToApprovalDetail();
  }, [navigate, isAssetView, goToAssetList, goToApprovalDetail]);

  return (
    <div className="media_viewer_breadcrumbs">
      <div className="media_viewer_breadcrumbs-wr">
        <Button className="media_viewer_btn b-only-icon" onClick={goBack}>
          <ArrowLeftWithoutLineIcon style={{ fontSize: 32 }} />
        </Button>
        <div className="media_viewer_breadcrumbs-navigation">
          <LgWidth>
            {!!approvalWorkspace && (
              <div
                className="media_viewer_breadcrumbs-item"
                onClick={goToApprovals}
              >
                <span className="item-name">Approvals</span>
              </div>
            )}
            {approvalWorkflow?.name && (
              <div
                className="media_viewer_breadcrumbs-item"
                onClick={goToApprovalDetail}
              >
                <Tooltip
                  title={approvalWorkflow.name}
                  overlayClassName="toggle_favorite_overlay  toggle_favorite_overlay--small"
                  overlayInnerStyle={{ position: 'relative', top: '-30px' }}
                >
                  <span className="item-name">{approvalWorkflow.name}</span>
                </Tooltip>
              </div>
            )}
            {!!approvalWorkspace && (
              <div
                className="media_viewer_breadcrumbs-item"
                onClick={isAssetView ? goToAssetList : undefined}
              >
                <span className="item-name">Review</span>
              </div>
            )}
          </LgWidth>
          {isAssetView && !!selectedVersion?.name && (
            <div className="media_viewer_breadcrumbs-item">
              <Tooltip
                title={`${selectedVersion.name}.${selectedVersion.extension}`}
                overlayClassName="toggle_favorite_overlay  toggle_favorite_overlay--small"
                overlayInnerStyle={{ position: 'relative', top: '-30px' }}
              >
                <span className="item-name">
                  {selectedVersion.name}.{selectedVersion.extension}
                </span>
              </Tooltip>
            </div>
          )}
        </div>
        {isAssetView && !!selectedVersion && (
          <DropdownAssetVersions external={true} />
        )}
        {isAssetView && !!assets?.asset?.permissions.uploadNewVersion && (
          <Button
            className="media_viewer_btn b-only-icon new-version-btn"
            onClick={() => {
              dispatch(
                toggleUploadAssetModal({
                  visible: true,
                  newVersionFromMediaViewer: true,
                  workspaceId: assets.workspaceId
                })
              );
            }}
          >
            <PlusIcon />
          </Button>
        )}
      </div>
    </div>
  );
});
